// src/pages/Learner/Dashboard.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  BookOpen,
  Briefcase,
  Brain,
  Target,
  Sparkles,
  Search,
  Copy,
  Edit,
  RefreshCw,
  ArrowDownCircle,
  PlusCircle,
  History,
  Upload,
  FileText,
  Image as ImageIcon,
  X,
  Paperclip,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useUserStore from "../../store/userStore";
import useAuthStore from "../../store/authStore";
import toast from "react-hot-toast";
import Analytics from "../Analytics/Analytics";
import UniversalContentRenderer from "../../components/UniversalContentRenderer";
import useBehaviorTracking from "../../hooks/useBehaviorTracking";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";


const LearnerDashboard = () => {
  const { user } = useUserStore();
  const { token } = useAuthStore();
  const {
    trackSessionStart,
    trackTopicSearch,
    trackSessionEnd,
    trackRoadmapGeneration,
  } = useBehaviorTracking();

  const [sessionId, setSessionId] = useState(null);
  const [activeTab, setActiveTab] = useState("career");
  const [showAnalytics, setShowAnalytics] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [reasoning, setReasoning] = useState("");
  const [showReasoning, setShowReasoning] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingInput, setEditingInput] = useState("");
  const [regeneratingMessageId, setRegeneratingMessageId] = useState(null);
  const [inputRows, setInputRows] = useState(1);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [error, setError] = useState(null);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isAtBottomRef = useRef(true);
  const extracted = {
    courses: [],
    jobs: [],
    skills: [],
  };

  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  // Auto-scroll only if user is already at the bottom
  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, reasoning]);

  // Scroll listener to show/hide the "scroll to bottom" button
  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      isAtBottomRef.current = isAtBottom;
      setShowScrollToBottom(!isAtBottom);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // restore session
  useEffect(() => {
    const saved = localStorage.getItem("skillmarg_chat");
    if (saved) {
      const parsed = JSON.parse(saved);
      setMessages(parsed.messages || []);
      setSessionId(parsed.sessionId || null);
    }
  }, []);
  useEffect(() => {
    localStorage.setItem(
      "skillmarg_chat",
      JSON.stringify({ messages, sessionId })
    );
  }, [messages, sessionId]);

  // Handle input auto-resizing
  useEffect(() => {
    const lines = input.split("\n").length;
    setInputRows(Math.min(lines, 5)); // Max 5 rows
  }, [input]);

  // File upload handler
  // Update the handleFileUpload function in frontend/src/pages/Learner/Dashboard.js

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const newFiles = [];

    for (const file of files) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not supported: ${file.name}`);
        continue;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File too large: ${file.name} (max 10MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);

        // Use new chat-specific endpoint instead of achievement upload
        const response = await fetch(
          `${API_URL}/users/upload-chat-file`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status}`);
        }

        const result = await response.json();

        newFiles.push({
          id: result.file_id, // Use file_id instead of achievement_id
          name: file.name,
          type: file.type,
          size: file.size,
          temp_path: result.temp_path, // Store temp path for analysis
          file_type: result.file_type,
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setIsUploading(false);

    if (newFiles.length > 0) {
      toast.success(`Uploaded ${newFiles.length} file(s) for analysis`);
    }
  };

  // Remove uploaded file
  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // File input change handler
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
    // Reset input
    e.target.value = "";
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const sendMessage = async (content, options = {}) => {
    trackTopicSearch(content, { sessionId });

    const startTime = Date.now();
    trackSessionStart("chat");

    const {
      isEditing = false,
      isRegenerating = false,
      messageId = null,
      assistantMessageId = null,
    } = options;

    const userText = content || input;

    // Clear main input only if we are not using an override from edit/regenerate
    if (!content) {
      setInput("");
    }

    const finalUserText = (!userText.trim() && uploadedFiles.length > 0) 
      ? "Please analyze the attached files." 
      : userText;

    if (!finalUserText.trim()) return;

    setError(null);
    setIsThinking(true);
    setReasoning("");

    // If regenerating, we don't want the "thinking" bubble if it's in-line
    if (isRegenerating) {
      setRegeneratingMessageId(assistantMessageId);
    }

    // If editing, find the message and slice the history
    let newMessages = [...messages];
    if (isEditing) {
      const messageIndex = newMessages.findIndex((m) => m.id === messageId);
      if (messageIndex !== -1) {
        newMessages = newMessages.slice(0, messageIndex);
      }
    }

    // Add user message to state, including uploaded files
    if (!isRegenerating) {
      const userId = Date.now();
      newMessages.push({
        id: userId,
        role: "user",
        content: finalUserText,
        files: [...uploadedFiles], // Include uploaded files
      });
    }

    // Clear uploaded files after sending
    setUploadedFiles([]);

    setMessages(newMessages);

    if (!token) {
      toast.error("Please login first");
      setIsThinking(false);
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId,
          messages: [{ role: "user", content: finalUserText }],
          files:
            uploadedFiles.length > 0
              ? uploadedFiles.map((f) => ({
                  achievement_id: f.achievement_id,
                  filename: f.name,
                  type: f.type,
                  temp_path: f.temp_path, // Add temp_path for chat files
                }))
              : undefined,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");

      if (isRegenerating) {
        // Find the message and prepare to add a new version
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id === assistantMessageId) {
              return {
                ...m,
                versions: [...m.versions, { content: "", sources: [] }],
              };
            }
            return m;
          })
        );
      } else {
        // Create a new assistant message with the versioning structure
        const newAssistantId = Date.now() + 1; // ensure unique id
        setMessages((prev) => [
          ...prev,
          {
            id: newAssistantId,
            role: "assistant",
            versions: [{ content: "", sources: [] }],
            currentVersion: 0,
          },
        ]);
      }

      let buffer = "";
      let invalidJSONBuffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split("\n");
        // The last line might be incomplete, keep it in the buffer
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue; // Skip empty lines

          // 🔧 FIX: Handle SSE format
          let jsonLine = line;
          if (line.startsWith("data: ")) {
            jsonLine = line.substring(6); // Remove "data: " prefix
          }

          // Check for Session ID
          if (jsonLine.startsWith("[SESSION_ID]")) {
            const newSessionId = jsonLine.replace("[SESSION_ID]", "").trim();
            setSessionId(newSessionId);
            continue;
          }

          // Skip SSE control messages
          if (jsonLine === "[DONE]") continue;

          let data;
          try {
            // Try to parse the current line (combined with buffer if exists)
            const textToParse = invalidJSONBuffer ? invalidJSONBuffer + jsonLine : jsonLine;
            data = JSON.parse(textToParse);
            // If successful, clear buffer
            invalidJSONBuffer = "";
          } catch (e) {
            // If parse fails, assume it's a split chunk and buffer it
            // Only buffer if it looks like it *could* be part of a JSON (simple heuristic or just always buffer)
            if (!invalidJSONBuffer) {
                // Should start with { for our messages
                if (jsonLine.trim().startsWith('{')) {
                    invalidJSONBuffer = jsonLine;
                } else {
                    console.error("Failed to parse stream line (dropped):", line, e);
                }
            } else {
                // Append to existing buffer
                invalidJSONBuffer += jsonLine;
            }
            continue;
          }

          setMessages((prev) => {
            const targetMessageId = isRegenerating
              ? assistantMessageId
              : prev[prev.length - 1].id;

            return prev.map((m) => {
              if (m.id === targetMessageId && m.role === "assistant") {
                const versionIndex = isRegenerating
                  ? m.versions.length - 1
                  : m.currentVersion;

                // Create a new version object with the updated content
                const updatedVersion = {
                  ...m.versions[versionIndex],
                };

                if (data.type === "thinking") {
                  setReasoning(data.content || "");
                } else if (data.type === "content") {
                  updatedVersion.content += data.content || "";
                } else if (data.type === "tool_result") {
                  if (data.url && data.favicon) {
                    updatedVersion.sources = [
                      ...(updatedVersion.sources || []),
                      { url: data.url, icon: data.favicon },
                    ];
                  }
                } else if (data.type === "error") {
                  updatedVersion.content += "\n\n> **Error:** " + (data.message || data.content || "Unknown error");
                }

                const newVersions = [...m.versions];
                newVersions[versionIndex] = updatedVersion;
                return { ...m, versions: newVersions };
              }
              return m;
            });
          });
        }
      }

      if (isRegenerating) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id === assistantMessageId) {
              return { ...m, currentVersion: m.versions.length - 1 };
            }
            return m;
          })
        );
      }
    } catch (e) {
      setError(e.message);
      toast.error("Failed to send message");
    } finally {
      setIsThinking(false);
      setEditingMessageId(null);
      setRegeneratingMessageId(null);
      setEditingInput("");
      const duration = (Date.now() - startTime) / 60000; // Convert to minutes
      trackSessionEnd("chat", duration);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setReasoning("");
    setShowReasoning(false);
    setIsThinking(false);
    setError(null);
    setSessionId(null);
    setUploadedFiles([]); // Clear uploaded files
    localStorage.removeItem("skillmarg_chat");
  };

  const handleEditMessage = (message) => {
    setEditingMessageId(message.id);
    setEditingInput(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingInput("");
  };

  const handleSaveEdit = () => {
    if (!editingInput.trim()) return;
    sendMessage(editingInput, { isEditing: true, messageId: editingMessageId });
  };

  const handleRegenerate = (assistantMessageId) => {
    const assistantMessageIndex = messages.findIndex(
      (m) => m.id === assistantMessageId
    );
    if (assistantMessageIndex > 0) {
      const userMessage = messages[assistantMessageIndex - 1];
      if (userMessage && userMessage.role === "user") {
        sendMessage(userMessage.content, {
          isRegenerating: true,
          assistantMessageId,
        });
      } else {
        toast.error("Could not find the corresponding user prompt.");
      }
    } else {
      toast.error("No previous prompt found to regenerate.");
    }
  };

  const handleCopyMessage = (message) => {
    const contentToCopy =
      message.role === "user"
        ? message.content
        : message.versions[message.currentVersion].content;
    navigator.clipboard.writeText(contentToCopy);
    toast.success("Copied to clipboard!");
  };

  const handleSwitchVersion = (messageId, direction) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id === messageId) {
          const newVersion = m.currentVersion + direction;
          if (newVersion >= 0 && newVersion < m.versions.length) {
            return { ...m, currentVersion: newVersion };
          }
        }
        return m;
      })
    );
  };

  // from earlier code – keep your tabs populated once roadmap data arrives
  // const extracted = {
  //   courses: Array.isArray(roadmap?.courses) ? roadmap.courses : [],
  //   jobs: Array.isArray(roadmap?.jobs) ? roadmap.jobs : [],
  //   skills: (roadmap?.skills || []).map((s) =>
  //     typeof s === "string" ? { skill_name: s } : s
  //   ),
  // };

  return (
    <div className="w-full min-h-screen flex flex-col">
      {/* Header */}
      <div className="max-w-5xl mx-auto w-full p-4">
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg p-6 text-white">
          <h1 className="text-3xl font-bold">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-primary-100 mt-2">
            Use our AI-powered career navigator to generate a personalized
            roadmap with document analysis capabilities.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm max-w-5xl mx-auto w-full px-4">
        <nav className="-mb-px flex space-x-8 overflow-x-auto border-b border-gray-200">
          {[
            { id: "career", name: "AI Roadmap", icon: Target },
            { id: "analytics", name: "Analytics", icon: BarChart3 },
            { id: "courses", name: "Recommended Courses", icon: BookOpen },
            { id: "jobs", name: "Job Opportunities", icon: Briefcase },
            { id: "skills", name: "Skills to Learn", icon: Brain },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flex-1 w-full"
        >
          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <div className="max-w-5xl mx-auto w-full px-4 py-6">
              <Analytics />
            </div>
          )}

          {/* === Main Chat Area === */}
          {activeTab === "career" && (
            <div className="max-w-5xl mx-auto w-full px-4 pt-4 pb-40 flex flex-col">
              <div ref={listRef} className="flex-1 space-y-6">
                {messages.map((m) => {
                  const isAssistant = m.role === "assistant";
                  const currentVersionData = isAssistant
                    ? m.versions[m.currentVersion]
                    : null;
                  const isStreaming =
                    isAssistant &&
                    currentVersionData &&
                    currentVersionData.content.length === 0;

                  return (
                    <div
                      key={m.id}
                      className={`group relative flex flex-col ${
                        m.role === "user" ? "items-end" : "items-start"
                      }`}
                    >
                      <div
                        className={`rounded-2xl px-4 py-3 max-w-[85%] shadow-sm ${
                          m.role === "user"
                            ? "bg-blue-600 text-white rounded-br-none"
                            : "bg-gray-100 text-gray-900 rounded-bl-none"
                        }`}
                      >
                        {/* Display uploaded files in user messages */}
                        {m.role === "user" && m.files && m.files.length > 0 && (
                          <div className="mb-3 space-y-2">
                            {m.files.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 bg-blue-700 rounded-lg p-2"
                              >
                                {file.type.startsWith("image/") ? (
                                  <ImageIcon className="h-4 w-4" />
                                ) : (
                                  <FileText className="h-4 w-4" />
                                )}
                                <span className="text-sm truncate">
                                  {file.name}
                                </span>
                                <span className="text-xs opacity-75">
                                  ({(file.size / 1024 / 1024).toFixed(1)}MB)
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Message Content */}
                        {editingMessageId === m.id ? (
                          <div className="flex flex-col gap-2 w-full">
                            <textarea
                              value={editingInput}
                              onChange={(e) => setEditingInput(e.target.value)}
                              className="bg-gray-700 text-white p-2 rounded-md focus:outline-none w-full"
                              rows={Math.min(
                                editingInput.split("\n").length,
                                10
                              )}
                              autoFocus
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={handleCancelEdit}
                                className="btn btn-ghost btn-sm"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveEdit}
                                className="btn btn-primary btn-sm"
                              >
                                Save & Submit
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {isAssistant ? (
                              <>
                                {currentVersionData.sources?.length > 0 && (
                                  <div className="flex items-center gap-2 mb-2">
                                    {currentVersionData.sources.map(
                                      (src, i) => {
                                        let faviconUrl = src.icon;
                                        if (!faviconUrl) {
                                          try {
                                            const domain = new URL(src.url)
                                              .hostname;
                                            faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
                                          } catch (e) {
                                            faviconUrl = ""; // Invalid URL
                                          }
                                        }
                                        return (
                                          <a
                                            key={i}
                                            href={src.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={src.url}
                                          >
                                            <img
                                              src={faviconUrl}
                                              alt="source"
                                              className="w-5 h-5 rounded-sm"
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          </a>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                                <UniversalContentRenderer
                                  content={currentVersionData.content}
                                />
                              </>
                            ) : (
                              m.content
                            )}
                          </>
                        )}
                      </div>

                      {/* Action Buttons & Versioning */}
                      {editingMessageId !== m.id && (
                        <div className="flex items-center gap-2 mt-1.5 h-8">
                          {isAssistant && m.versions.length > 1 && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <button
                                onClick={() => handleSwitchVersion(m.id, -1)}
                                disabled={m.currentVersion === 0}
                                className="p-1 rounded-full disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700"
                              >
                                &lt;
                              </button>
                              <span>
                                {m.currentVersion + 1} / {m.versions.length}
                              </span>
                              <button
                                onClick={() => handleSwitchVersion(m.id, 1)}
                                disabled={
                                  m.currentVersion === m.versions.length - 1
                                }
                                className="p-1 rounded-full disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700"
                              >
                                &gt;
                              </button>
                            </div>
                          )}

                          {m.role === "user" ? (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                              <button
                                onClick={() => handleEditMessage(m)}
                                className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4 text-gray-500" />
                              </button>
                              <button
                                onClick={() => handleCopyMessage(m)}
                                className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                title="Copy"
                              >
                                <Copy className="h-4 w-4 text-gray-500" />
                              </button>
                            </div>
                          ) : (
                            <>
                              {!isStreaming &&
                                regeneratingMessageId !== m.id && (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleRegenerate(m.id)}
                                      disabled={isThinking}
                                      className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                      title="Regenerate"
                                    >
                                      <RefreshCw className="h-4 w-4 text-gray-500" />
                                    </button>
                                    <button
                                      onClick={() => handleCopyMessage(m)}
                                      className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                      title="Copy"
                                    >
                                      <Copy className="h-4 w-4 text-gray-500" />
                                    </button>
                                  </div>
                                )}
                            </>
                          )}
                          {regeneratingMessageId === m.id && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse"></div>
                              <span>Regenerating...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {isThinking && !regeneratingMessageId && (
                  <div className="flex justify-start">
                    <div className="px-4 py-3 bg-gray-100 rounded-2xl">
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse [animation-delay:-0.3s]"></div>
                        <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse [animation-delay:-0.15s]"></div>
                        <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse"></div>
                        <span className="text-sm ml-1">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                {reasoning && (
                  <div className="flex justify-start">
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm max-w-[85%]">
                      <button
                        onClick={() => setShowReasoning(!showReasoning)}
                        className="font-semibold text-gray-700 underline"
                      >
                        {showReasoning ? "Hide reasoning" : "Show reasoning"}
                      </button>
                      {showReasoning && (
                        <pre className="whitespace-pre-wrap mt-2 text-gray-700 text-xs">
                          {reasoning}
                        </pre>
                      )}
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Scroll to bottom button */}
              {showScrollToBottom && (
                <button
                  onClick={scrollToBottom}
                  className="fixed bottom-24 right-8 z-20 bg-white dark:bg-gray-700 p-2 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                >
                  <ArrowDownCircle className="h-6 w-6 text-primary-600" />
                </button>
              )}

              {/* Enhanced Fixed input bar with file upload */}
              <div
                className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white to-white/80 dark:from-gray-900 dark:to-gray-900/80 backdrop-blur lg:pl-64"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="max-w-5xl mx-auto w-full px-4 py-3">
                  {/* Display uploaded files preview ABOVE the input */}
                  {uploadedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-2 border-gray-200 dark:border-gray-700">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-2 bg-blue-100 dark:bg-blue-900 rounded-lg px-3 py-2 shadow-sm"
                        >
                          {file.type.startsWith("image/") ? (
                            <ImageIcon className="h-4 w-4 text-blue-600" />
                          ) : (
                            <FileText className="h-4 w-4 text-blue-600" />
                          )}
                          <span className="text-sm text-blue-800 dark:text-blue-200 truncate max-w-32">
                            {file.name}
                          </span>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                            title="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input area container */}
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative flex-grow max-w-3xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl shadow-sm flex items-center">
                      {/* Sparkles icon outside the input area - on the left side */}
                      <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />

                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,.pdf"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />

                      {/* File upload button */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute left-12 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Attach files (images, PDFs)"
                        disabled={isUploading}
                      >
                        <Paperclip className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                      </button>

                      <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={
                          uploadedFiles.length > 0
                            ? "Describe what you'd like me to do with these files..."
                            : sessionId
                            ? "Ask a follow-up question…"
                            : "e.g., 'I want to become a data scientist…'"
                        }
                        className="bg-transparent w-full pl-20 pr-20 py-3 text-lg focus:outline-none resize-none dark:text-white"
                        rows={inputRows}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                      />
                      <button
                        type="submit"
                        className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-primary btn-md rounded-xl"
                        disabled={
                          isUploading ||
                          (!input.trim() && uploadedFiles.length === 0)
                        }
                      >
                        {isUploading ? (
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Search className="h-5 w-5" />
                        )}
                        <span className="hidden sm:inline ml-2">
                          {sessionId ? "Ask" : "Generate"}
                        </span>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleNewChat}
                      className="btn btn-ghost btn-lg"
                      title="New Chat"
                    >
                      <PlusCircle className="h-6 w-6" />
                    </button>
                    <Link
                      to="/history"
                      className="btn btn-ghost btn-lg"
                      title="Chat History"
                    >
                      <History className="h-6 w-6" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === Recommended Courses === */}
          {activeTab === "courses" && (
            <div className="max-w-5xl mx-auto w-full px-4 py-6">
              {extracted.courses?.length ? (
                extracted.courses.map((c, i) => (
                  <div key={i} className="card mb-3 p-4">
                    <h4 className="font-semibold mb-1">
                      {c.title || c.name || `Course ${i + 1}`}
                    </h4>
                    <p className="text-sm text-gray-600">{c.description}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 card bg-gray-50 dark:bg-gray-800">
                  <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">
                    No Recommended Courses
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Generate a roadmap to see course recommendations.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* === Job Opportunities === */}
          {activeTab === "jobs" && (
            <div className="max-w-5xl mx-auto w-full px-4 py-6">
              {extracted.jobs?.length ? (
                extracted.jobs.map((job, i) => (
                  <div key={i} className="card mb-3 p-4">
                    <h4 className="font-semibold mb-1">
                      {job.title || `Job ${i + 1}`}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {job.company || job.location}
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 card bg-gray-50 dark:bg-gray-800">
                  <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">
                    No Job Opportunities
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Generate a roadmap to see relevant job opportunities.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* === Skills to Learn === */}
          {activeTab === "skills" && (
            <div className="max-w-5xl mx-auto w-full px-4 py-6">
              {extracted.skills?.length ? (
                extracted.skills.map((s, i) => (
                  <div key={i} className="card mb-3 p-4">
                    <h4 className="font-semibold">
                      {s.skill_name || s.name || `Skill ${i + 1}`}
                    </h4>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 card bg-gray-50 dark:bg-gray-800">
                  <Brain className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">
                    No Skills Recommended
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Generate a roadmap to see recommended skills to learn.
                  </p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default LearnerDashboard;
